/**
 * The marketing page.
 *
 * Authorised by implementation-brief §12 ("apps/web (marketing + shared-list web
 * fallback)") and product-concept §12, which gives the positioning line ("Every
 * to-do app helps you start. Carn helps you finish.") and names the ethics
 * commitments that are genuinely differentiating rather than commoditised claims
 * (§11 objection 1: "shame-free" is what every 2026 ADHD app markets; differentiation
 * must be structural). Deliberately small and true: no pricing, no testimonials, no
 * screenshots, no App Store links, no launch date — §12 forbids fake urgency and dark
 * patterns, and a page implying a shipping product would be exactly that.
 */
import type { CopyKey } from '@carn/copy';
import { useCopy } from '../theme/CopyProvider';
import { useTheme } from '../theme/ThemeProvider';
import { LiteralToggle } from './LiteralToggle';
import { SiteFooter } from '../site/SiteFooter';
import { text } from '../site/prose';

export function MarketingPage() {
  const theme = useTheme();
  const { t } = useCopy();
  const c = theme.colours;
  const s = theme.space;
  const type = theme.type;

  const section = (titleKey: CopyKey, rows: CopyKey[]) => (
    <section style={{ display: 'flex', flexDirection: 'column', gap: s.md }}>
      <h2 style={{ ...text(type.title), fontSize: type.title.fontSize * 0.7, color: c.text, margin: 0 }}>
        {t(titleKey)}
      </h2>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: s.sm }}>
        {rows.map((key) => (
          <li
            key={key}
            style={{
              ...text(type.body),
              color: c.textSecondary,
              background: c.surface,
              border: `${s.hairline}px solid ${c.border}`,
              borderRadius: s.radius.md,
              padding: s.cardPadding,
            }}
          >
            {t(key)}
          </li>
        ))}
      </ul>
    </section>
  );

  return (
    <main
      style={{
        minHeight: '100vh',
        background: c.background,
        color: c.text,
        fontFamily: type.family,
      }}
    >
      <div style={{ maxWidth: 640, margin: '0 auto', padding: s.screenPadding, display: 'flex', flexDirection: 'column', gap: s.xxl }}>
        <header style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <LiteralToggle />
        </header>

        <section style={{ display: 'flex', flexDirection: 'column', gap: s.md }}>
          <h1 style={{ ...text(type.title), fontSize: type.title.fontSize * 1.3, color: c.text, margin: 0 }}>
            {t('marketing.hero')}
          </h1>
          <p style={{ ...text(type.body), color: c.textSecondary, margin: 0 }}>{t('marketing.heroSub')}</p>
          <p style={{ ...text(type.quiet), color: c.textTertiary, margin: 0 }}>{t('marketing.audience')}</p>
        </section>

        {section('marketing.whatItDoes.title', [
          'marketing.whatItDoes.capture',
          'marketing.whatItDoes.resume',
          'marketing.whatItDoes.sharedLists',
        ])}

        {section('marketing.ethics.title', [
          'marketing.ethics.noStreaks',
          'marketing.ethics.noOverdue',
          'marketing.ethics.sensoryFree',
          'marketing.ethics.cancelTwoTaps',
        ])}

        <footer style={{ display: 'flex', flexDirection: 'column', gap: s.lg, paddingBottom: s.xxl }}>
          <p style={{ ...text(type.quiet), color: c.textTertiary, margin: 0 }}>{t('marketing.nonClinical')}</p>
          {/* The two URLs App Store Connect requires are reachable from the Marketing URL,
              which is the page App Review lands on first. */}
          <SiteFooter current="/" />
        </footer>
      </div>
    </main>
  );
}
